import React, { useState, useRef, useCallback, useEffect } from "react";
import { ChevronDown, ChevronUp, GripHorizontal } from "lucide-react";

type FloatingPanelProps = {
  title: string;
  children: React.ReactNode;
  defaultPosition?: { x: number; y: number };
  defaultMinimized?: boolean;
  width?: number;
  maxHeight?: number;
  zIndex?: number;
  /** Registry of all floating panels for snap-to behavior */
  panelRegistry?: React.MutableRefObject<Map<string, { x: number; y: number; width: number; height: number; minimized: boolean }>>;
  panelId?: string;
  onClose?: () => void;
  /** Ukryj przycisk ▲/▼ (panel zawsze otwarty). */
  hideMinimizeButton?: boolean;
  /**
   * Overflow wewnątrz panelu. Default "auto" — content scrolluje się gdy
   * przekracza maxHeight. Gdy content zawiera popup'y (np. dropdown
   * autocomplete z position:absolute), użyj "visible" żeby nie były clipped.
   */
  contentOverflow?: "auto" | "visible";
  /** Zmiana wartości rozwija zminimalizowany panel (np. nowa analiza do pokazania). */
  expandSignal?: unknown;
};

const SNAP_DISTANCE = 20;
const MINIMIZED_HEIGHT = 36;

function FloatingPanel({
  title,
  children,
  defaultPosition = { x: 12, y: 12 },
  defaultMinimized = false,
  width = 300,
  maxHeight = 500,
  zIndex = 1000,
  panelRegistry,
  panelId = title,
  onClose,
  hideMinimizeButton = false,
  contentOverflow = "auto",
  expandSignal,
}: FloatingPanelProps) {
  const [position, setPosition] = useState(defaultPosition);
  const [minimized, setMinimized] = useState(defaultMinimized);
  useEffect(() => {
    if (expandSignal) setMinimized(false);
  }, [expandSignal]);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (panelRegistry) {
      const rect = panelRef.current;
      panelRegistry.current.set(panelId, {
        x: position.x,
        y: position.y,
        width,
        height: minimized ? MINIMIZED_HEIGHT : (rect?.offsetHeight || 200),
        minimized,
      });
    }
    return () => { panelRegistry?.current.delete(panelId); };
  }, [position.x, position.y, minimized, panelId]);

  const snapPosition = useCallback((x: number, y: number): { x: number; y: number } => {
    if (!panelRegistry || !minimized) return { x, y };
    const myHeight = MINIMIZED_HEIGHT;
    let snappedX = x;
    let snappedY = y;
    for (const [id, other] of panelRegistry.current) {
      if (id === panelId) continue;
      // Snap below other panel
      const belowY = other.y + other.height + 4;
      if (Math.abs(y - belowY) < SNAP_DISTANCE && Math.abs(x - other.x) < SNAP_DISTANCE * 2) {
        snappedX = other.x;
        snappedY = belowY;
      }
      // Snap above other panel
      const aboveY = other.y - myHeight - 4;
      if (Math.abs(y - aboveY) < SNAP_DISTANCE && Math.abs(x - other.x) < SNAP_DISTANCE * 2) {
        snappedX = other.x;
        snappedY = aboveY;
      }
      // Snap right of other panel
      const rightX = other.x + other.width + 4;
      if (Math.abs(x - rightX) < SNAP_DISTANCE && Math.abs(y - other.y) < SNAP_DISTANCE * 2) {
        snappedX = rightX;
        snappedY = other.y;
      }
    }
    return { x: snappedX, y: snappedY };
  }, [panelRegistry, minimized, panelId]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [position]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return;
    e.preventDefault();
    const container = panelRef.current?.parentElement;
    const maxX = (container?.clientWidth || window.innerWidth) - width;
    const maxY = (container?.clientHeight || window.innerHeight) - MINIMIZED_HEIGHT;
    const rawX = Math.max(0, Math.min(e.clientX - dragOffset.current.x, maxX));
    const rawY = Math.max(0, Math.min(e.clientY - dragOffset.current.y, maxY));
    const snapped = snapPosition(rawX, rawY);
    setPosition(snapped);
  }, [dragging, snapPosition, width]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging) return;
    setDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, [dragging]);

  return (
    <div
      ref={panelRef}
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        width,
        zIndex: dragging ? zIndex + 100 : zIndex,
        transition: dragging ? "none" : "box-shadow 0.2s",
        userSelect: dragging ? "none" : "auto",
      }}
      className={`bg-white rounded-lg border border-gray-300 shadow-lg ${dragging ? "shadow-xl ring-2 ring-blue-300" : ""}`}
    >
      {/* Title bar — drag handle */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-t-lg border-b border-gray-200 cursor-grab active:cursor-grabbing select-none"
        style={{ touchAction: "none" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <GripHorizontal className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="text-xs font-semibold text-gray-700 truncate">{title}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!hideMinimizeButton && (
            <button
              type="button"
              onClick={() => setMinimized((m) => !m)}
              className="p-0.5 rounded hover:bg-gray-200 text-gray-500"
              title={minimized ? "Rozwiń" : "Zwiń"}
            >
              {minimized ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500"
              title="Zamknij"
            >
              <span className="text-sm leading-none">✕</span>
            </button>
          )}
        </div>
      </div>
      {/* Content */}
      {!minimized && (
        <div
          style={
            contentOverflow === "visible"
              ? { overflow: "visible" }
              : { maxHeight, overflow: "auto" }
          }
          className="p-0"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default FloatingPanel;
